import React from 'react';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import classnames from 'classnames';

import { Image, YoutubeVideo } from 'components/ui';
import { IMAGES } from 'constants/images';
import { getDocumentToReactComponentsOptions } from 'helpers/LandingPageHelpers';

import type { IContentfulImage, IContentfulRichText, Nullable } from 'types';

import styles from './AlphaTemplateMediaBlock.module.scss';

interface Props {
  className?: string,
  hasGrayBackground?: boolean,
  title: Nullable<IContentfulRichText>,
  description: Nullable<IContentfulRichText>,
  image?: IContentfulImage,
  video?: string,
  mediaPosition?: 'left' | 'right',
  isMobilePreview: boolean
}

const AlphaTemplateMediaBlock = (props: Props) => {

  const {
    className, hasGrayBackground,
    title, description,
    image, video, mediaPosition,
    isMobilePreview
  } = props;

  const { json: titleJson } = title || {};
  const { json: descriptionJson } = description || {};

  const isReversed = mediaPosition === 'left';
  const hasMedia = image || video;
  const hasText = !!description || !!title;

  const documentToReactOptions = React.useMemo(() => getDocumentToReactComponentsOptions({ isMobilePreview }), [isMobilePreview]);

  const rootClasses = classnames(
    styles.root,
    className,
    { [styles.gray]: hasGrayBackground }
  );

  const innerClasses = classnames(
    styles.inner,
    {
      [styles.reverse]: isReversed,
      [styles.hasMedia]: hasMedia,
      [styles.onlyMedia]: hasMedia && !hasText,
      [styles.onlyText]: !hasMedia && hasText
    }
  );

  return (
    <div className={rootClasses}>
      <div className={innerClasses}>
        {
          hasText && (
            <div className={classnames(styles.textContainer)}>
              {
                title && (
                  <div className={styles.title}>
                    {documentToReactComponents(titleJson, documentToReactOptions)}
                  </div>
                )
              }
              {
                description && (
                  <div className={styles.description}>
                    {documentToReactComponents(descriptionJson, documentToReactOptions)}
                  </div>
                )
              }
            </div>
          )
        }
        {
          hasMedia && (
            <div className={classnames(styles.mediaContainer)}>
              {
                !video
                  ? (
                    <div className={styles.image}>
                      <Image
                        height={540}
                        width={960}
                        src={image?.url || IMAGES.PLACEHOLDERS.GENERIC}
                      />

                    </div>
                  ) : (
                    <YoutubeVideo
                      url={video}
                      className={styles.video}
                    />
                  )
              }
            </div>
          )
        }
      </div>
    </div>
  );
};

export default AlphaTemplateMediaBlock;
